/**
 * 新增补消对话框（ADM-33，makeup-deduction）。
 *
 * 三步式：选课次（过去已结束 SCHEDULED/COMPLETED）→ 勾学生（已消禁勾，
 * 已冲正/曾跳过可再补，默认勾选全部可补）→ 填原因（必填中文 ≤255）提交。
 * 成功展示逐生结果；409 业务拒绝（含已消学生等）直接提示后端中文文案。
 */
import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, DatePicker, Descriptions, Input, Select, Space, Steps, Table, Tag, Tooltip, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { DraggableModal } from '../../components/DraggableModal';
import { EllipsisText } from '../../components/EllipsisCell';
import { HasPermission } from '../../auth/HasPermission';
import { MakeupAddMemberModal } from './MakeupAddMemberModal';
import {
  makeupDeductionApi,
  type MakeupEligibleSession,
  type MakeupRoster,
  type MakeupRosterStudent,
  type MakeupSubmitResult,
} from '../../api/makeup-deduction.api';
import { classesApi, type ClassListItem } from '../../api/classes.api';
import { useGlobalCampusScope } from '../../auth/GlobalCampusContext';

const { RangePicker } = DatePicker;
const { Text } = Typography;

/** 消课状态 → 展示标签（第二步勾选依据）。 */
const stateLabel: Record<MakeupRosterStudent['deductionState'], { text: string; color: string }> = {
  DEDUCTED: { text: '已消', color: 'default' },
  REVERSED: { text: '已冲正', color: 'blue' },
  SKIPPED: { text: '曾跳过', color: 'orange' },
  NONE: { text: '未消', color: 'green' },
};

/** 提交结果 → 展示标签。 */
const resultLabel: Record<string, { text: string; color: string }> = {
  DEDUCTED: { text: '成功扣减', color: 'success' },
  SKIPPED_NO_BALANCE: { text: '余额不足', color: 'error' },
  SKIPPED_ACCOUNT_INVALID: { text: '账户不可用', color: 'error' },
  PERIOD_COVERED: { text: '期限卡覆盖', color: 'blue' },
};

function formatTime(value: string): string {
  return dayjs(value).format('HH:mm');
}

export interface MakeupDeductionCreateModalProps {
  open: boolean;
  onClose: () => void;
  /** 提交成功回调（页面刷新补消记录列表）。 */
  onSubmitted: () => void;
}

/** 新增补消三步式对话框。 */
export function MakeupDeductionCreateModal({
  open,
  onClose,
  onSubmitted,
}: MakeupDeductionCreateModalProps) {
  const [step, setStep] = useState<'session' | 'roster' | 'reason' | 'done'>('session');
  // ── 第一步：合格课次搜索 ──
  const [classes, setClasses] = useState<ClassListItem[]>([]);
  const [classId, setClassId] = useState<string | undefined>();
  const [dateFrom, setDateFrom] = useState<string | undefined>();
  const [dateTo, setDateTo] = useState<string | undefined>();
  const [sessions, setSessions] = useState<MakeupEligibleSession[]>([]);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [sessionPage, setSessionPage] = useState(1);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [selectedSession, setSelectedSession] = useState<MakeupEligibleSession | null>(null);
  // ── 第二步：名单勾选 ──
  const [roster, setRoster] = useState<MakeupRoster | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  // O-22：手动加入弹窗开关与名单刷新键（加入成功后重拉名单）
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [rosterRefreshKey, setRosterRefreshKey] = useState(0);
  // ── 第三步：原因与结果 ──
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<MakeupSubmitResult | null>(null);

  // O-28：班级选项随全局校区联动（补齐 O-21 D5 一致性，全部校区不带参）
  const { scope } = useGlobalCampusScope();
  const activeCampusId = scope === 'ALL' ? undefined : scope;
  useEffect(() => {
    if (!open) return;
    classesApi
      .listClasses({
        page: 1,
        pageSize: 100,
        ...(activeCampusId ? { campusId: activeCampusId } : {}),
      })
      .then((res) => setClasses(res.items))
      .catch(() => message.error('班级列表加载失败'));
  }, [open, activeCampusId]);

  // 班级选项随校区刷新后，清理不在新选项集内的已选班级（D5 越界清理同语义）
  useEffect(() => {
    setClassId((prev) => (prev && !classes.some((item) => item.id === prev) ? undefined : prev));
  }, [classes]);

  const loadSessions = useCallback(async () => {
    setSessionLoading(true);
    try {
      const res = await makeupDeductionApi.listEligibleSessions({
        classId,
        dateFrom,
        dateTo,
        page: sessionPage,
        pageSize: 10,
      });
      setSessions(res.items);
      setSessionTotal(res.total);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '合格课次加载失败');
    } finally {
      setSessionLoading(false);
    }
  }, [classId, dateFrom, dateTo, sessionPage]);

  useEffect(() => {
    if (open && step === 'session') void loadSessions();
  }, [open, step, loadSessions]);

  // 进入名单步拉取 roster，默认勾选全部可补学生
  useEffect(() => {
    if (!open || step !== 'roster' || !selectedSession) return;
    let cancelled = false;
    setRosterLoading(true);
    makeupDeductionApi
      .getRoster(selectedSession.id)
      .then((data) => {
        if (cancelled) return;
        setRoster(data);
        setSelectedStudentIds(
          data.students.filter((s) => s.selectable).map((s) => s.studentId),
        );
      })
      .catch((error) => {
        if (cancelled) return;
        message.error(error instanceof Error ? error.message : '补消名单加载失败');
        setStep('session');
      })
      .finally(() => {
        if (!cancelled) setRosterLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, step, selectedSession, rosterRefreshKey]);

  function resetAll() {
    setStep('session');
    setClassId(undefined);
    setDateFrom(undefined);
    setDateTo(undefined);
    setSessionPage(1);
    setSessions([]);
    setSessionTotal(0);
    setSelectedSession(null);
    setRoster(null);
    setSelectedStudentIds([]);
    setReason('');
    setResult(null);
  }

  function handleClose() {
    resetAll();
    onClose();
  }

  async function handleSubmit() {
    if (!selectedSession || selectedStudentIds.length === 0) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      message.warning('请填写补消原因');
      return;
    }
    setSubmitting(true);
    try {
      const res = await makeupDeductionApi.submitMakeupDeduction(
        selectedSession.id,
        selectedStudentIds,
        trimmed,
      );
      setResult(res);
      setStep('done');
      onSubmitted();
    } catch (error) {
      // 409 等业务拒绝：后端中文文案已含学生名，直接提示
      message.error(error instanceof Error ? error.message : '补消提交失败');
    } finally {
      setSubmitting(false);
    }
  }

  const sessionColumns: ColumnsType<MakeupEligibleSession> = [
    { title: '课次日期', dataIndex: 'sessionDate', width: 110 },
    {
      title: '时间',
      key: 'time',
      width: 110,
      render: (_, r) => `${formatTime(r.startAt)} - ${formatTime(r.endAt)}`,
    },
    {
      title: '班级',
      dataIndex: 'className',
      width: 140,
      ellipsis: true,
      render: (value: string) => <EllipsisText value={value} />,
    },
    {
      title: '课程 SKU',
      dataIndex: 'skuName',
      width: 140,
      ellipsis: true,
      render: (value: string) => <EllipsisText value={value} />,
    },
    { title: '教师', dataIndex: 'teacherName', width: 90 },
    {
      title: '状态',
      dataIndex: 'status',
      width: 95,
      render: (value: string) =>
        value === 'SCHEDULED' ? (
          <Tag color="warning">整课漏消</Tag>
        ) : (
          <Tag>已完结</Tag>
        ),
    },
    {
      title: '已消 / 在班',
      key: 'deducted',
      width: 100,
      align: 'center',
      render: (_, r) => `${r.deductedCount} / ${r.studentCount}`,
    },
  ];

  const rosterColumns: ColumnsType<MakeupRosterStudent> = [
    {
      title: '姓名',
      dataIndex: 'name',
      width: 130,
      render: (value: string, record: MakeupRosterStudent) => (
        <Space size={4}>
          <span>{value}</span>
          {record.addedManually && (
            <Tooltip
              title={`手动加入：${record.addedReason ?? ''}（操作人：${record.addOperatorName ?? '—'}）`}
            >
              <Tag color="purple" data-testid="roster-added-tag">
                手动加入
              </Tag>
            </Tooltip>
          )}
        </Space>
      ),
    },
    { title: '学号', dataIndex: 'studentNo', width: 120 },
    {
      title: '将扣类别',
      dataIndex: 'deductLessonKind',
      width: 95,
      render: (
        value: MakeupRosterStudent['deductLessonKind'],
        record: MakeupRosterStudent,
      ) => {
        if (record.periodCoverage && record.periodCoverage !== 'NONE') {
          return <span style={{ color: '#1677ff', fontWeight: 500 }}>期限卡覆盖</span>;
        }
        if (value === 'GIFTED') return '赠送课';
        if (value === 'FORMAL') return '正式课';
        // 概念口径：账户 = 学生 × 课程SKU 子账户，课时不足是课时维度问题，不得表述为「无账户」
        if (record.accountState === 'EXPIRED_VOID') {
          return <Typography.Text type="warning">账户已失效</Typography.Text>;
        }
        if (record.accountState === 'BALANCE_EXHAUSTED') {
          return <Typography.Text type="warning">课时不足（余 0）</Typography.Text>;
        }
        // 跨 SKU 学生：账户挂在其它课程上（多由直连库调数产生），透出去向避免误读为未购课
        if (record.crossSkuAccounts?.length) {
          const detail = record.crossSkuAccounts
            .map(
              (a) =>
                `${a.skuName}：正式 ${a.formalAvailable} / 赠送 ${a.giftedAvailable}`,
            )
            .join('；');
          return (
            <Tooltip title={`本课程无可扣课时，课时在其它课程——${detail}`}>
              <Typography.Text type="warning">课时在其它课程</Typography.Text>
            </Tooltip>
          );
        }
        return '无本课程课时账户';
      },
    },
    {
      title: '将扣池余额',
      dataIndex: 'balance',
      width: 85,
      align: 'center',
      render: (value: number | null, record) =>
        value === null ? (
          '—'
        ) : (
          <Text type={record.balanceWarning ? 'danger' : undefined}>{value}</Text>
        ),
    },
    {
      title: '消课状态',
      dataIndex: 'deductionState',
      width: 90,
      render: (value: MakeupRosterStudent['deductionState']) => (
        <Tag color={stateLabel[value].color}>{stateLabel[value].text}</Tag>
      ),
    },
    {
      title: '上次跳过原因',
      dataIndex: 'lastFailReason',
      width: 160,
      ellipsis: true,
      render: (value: string | null) => <EllipsisText value={value ?? ''} empty="—" />,
    },
  ];

  const resultColumns: ColumnsType<MakeupSubmitResult['items'][number]> = [
    {
      title: '姓名',
      dataIndex: 'studentId',
      width: 90,
      render: (value: string) =>
        roster?.students.find((s) => s.studentId === value)?.name ?? value,
    },
    {
      title: '结果',
      dataIndex: 'result',
      width: 100,
      render: (value: string) => {
        const label = resultLabel[value] ?? { text: value, color: 'default' };
        return <Tag color={label.color}>{label.text}</Tag>;
      },
    },
    {
      title: '扣减数量',
      dataIndex: 'quantity',
      width: 90,
      align: 'center',
      render: (value: number, record) =>
        record.result === 'PERIOD_COVERED' ? '0（期限卡抵扣）' : value,
    },
    {
      title: '失败原因',
      dataIndex: 'failReason',
      width: 160,
      render: (value: string | null) => value ?? '—',
    },
    {
      title: '流水号',
      dataIndex: 'ledgerEntryId',
      width: 170,
      ellipsis: true,
      render: (value: string | null) => <EllipsisText value={value ?? ''} empty="—" />,
    },
  ];

  const stepIndex = step === 'session' ? 0 : step === 'roster' ? 1 : 2;

  const footer =
    step === 'session'
      ? [
          <Button key="cancel" onClick={handleClose}>
            取消
          </Button>,
          <Button
            key="next"
            type="primary"
            disabled={!selectedSession}
            onClick={() => setStep('roster')}
            data-testid="btn-to-roster"
          >
            下一步
          </Button>,
        ]
      : step === 'roster'
        ? [
            <Button key="prev" onClick={() => setStep('session')}>
              上一步
            </Button>,
            <Button
              key="next"
              type="primary"
              disabled={selectedStudentIds.length === 0}
              onClick={() => setStep('reason')}
              data-testid="btn-to-reason"
            >
              下一步
            </Button>,
          ]
        : step === 'reason'
          ? [
              <Button key="prev" onClick={() => setStep('roster')}>
                上一步
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={submitting}
                onClick={() => void handleSubmit()}
                data-testid="btn-submit-makeup"
              >
                提交补消
              </Button>,
            ]
          : [
              <Button key="done" type="primary" onClick={handleClose} data-testid="btn-makeup-done">
                完成
              </Button>,
            ];

  return (
    <DraggableModal
      title="新增补消"
      open={open}
      onCancel={handleClose}
      width={960}
      destroyOnHidden
      footer={footer}
      data-testid="makeup-create-modal"
    >
      <Steps
        current={stepIndex}
        items={[{ title: '选择课次' }, { title: '勾选学生' }, { title: '填写原因' }]}
        style={{ marginBottom: 16 }}
      />

      {step === 'session' && (
        <>
          <Space wrap size={12} style={{ marginBottom: 12 }}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="班级"
              aria-label="班级"
              style={{ width: 180 }}
              value={classId}
              options={classes.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => {
                setClassId(value);
                setSessionPage(1);
              }}
            />
            <RangePicker
              value={
                dateFrom && dateTo ? [dayjs(dateFrom), dayjs(dateTo)] : null
              }
              placeholder={['上课日期开始', '上课日期结束']}
              onChange={(value) => {
                setDateFrom(value?.[0]?.format('YYYY-MM-DD'));
                setDateTo(value?.[1]?.format('YYYY-MM-DD'));
                setSessionPage(1);
              }}
            />
          </Space>
          <Table
            size="small"
            rowKey="id"
            loading={sessionLoading}
            columns={sessionColumns}
            dataSource={sessions}
            data-testid="makeup-session-table"
            rowSelection={{
              type: 'radio',
              selectedRowKeys: selectedSession ? [selectedSession.id] : [],
              onSelect: (record) => setSelectedSession(record),
              onChange: (_keys, records) => setSelectedSession(records[0] ?? null),
            }}
            onRow={(record) => ({
              onClick: () => setSelectedSession(record),
            })}
            pagination={{
              current: sessionPage,
              pageSize: 10,
              total: sessionTotal,
              size: 'small',
              showTotal: (count) => `共 ${count} 个课次`,
              onChange: (next) => setSessionPage(next),
            }}
          />
        </>
      )}

      {step === 'roster' && selectedSession && (
        <>
          <Descriptions bordered size="small" column={3} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="课次">
              {selectedSession.sessionDate} {formatTime(selectedSession.startAt)}
            </Descriptions.Item>
            <Descriptions.Item label="班级">{selectedSession.className}</Descriptions.Item>
            <Descriptions.Item label="教师">{selectedSession.teacherName}</Descriptions.Item>
          </Descriptions>
          <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'flex-end' }}>
            {/* 手动加人需 ADM-33 ADD_MEMBER 专有动词（O-22）；越权由后端 403 兜底 */}
            <HasPermission menuKey="ADM-33" action="ADD_MEMBER">
              <Button
                size="small"
                onClick={() => setAddMemberOpen(true)}
                data-testid="btn-add-member"
              >
                手动加人
              </Button>
            </HasPermission>
          </div>
          <Table
            size="small"
            rowKey="studentId"
            loading={rosterLoading}
            columns={rosterColumns}
            dataSource={roster?.students ?? []}
            data-testid="makeup-roster-table"
            rowSelection={{
              selectedRowKeys: selectedStudentIds,
              getCheckboxProps: (record) => ({ disabled: !record.selectable }),
              onChange: (keys) => setSelectedStudentIds(keys as string[]),
            }}
            pagination={false}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            已选 {selectedStudentIds.length} 人（已消学生不可补；已冲正/曾跳过学生可再补）
          </Text>
        </>
      )}

      {step === 'reason' && selectedSession && (
        <>
          <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
            <Descriptions.Item label="补消课次">
              {selectedSession.sessionDate} {formatTime(selectedSession.startAt)} ·{' '}
              {selectedSession.className}（{selectedSession.skuName}）
            </Descriptions.Item>
            <Descriptions.Item label="补消学生">
              共 {selectedStudentIds.length} 人，每人扣减 1 课时
            </Descriptions.Item>
          </Descriptions>
          <Input.TextArea
            rows={4}
            maxLength={255}
            showCount
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="请填写补消原因（必填，如：漏消补扣：老师忘点名）"
            aria-label="补消原因"
            data-testid="makeup-reason-input"
          />
        </>
      )}

      {step === 'done' && result && (
        <>
          <Alert
            type={result.failedCount > 0 ? 'warning' : 'success'}
            showIcon
            style={{ marginBottom: 12 }}
            message={`补消完成：成功 ${result.successCount} 人，失败 ${result.failedCount} 人`}
            description={`批次号：${result.batchId}`}
            data-testid="makeup-result-alert"
          />
          <Table
            size="small"
            rowKey="studentId"
            columns={resultColumns}
            dataSource={result.items}
            pagination={false}
            data-testid="makeup-result-table"
          />
        </>
      )}

      {/* O-22 手动加入：成功后刷新名单（新增行带「手动加入」标记） */}
      <MakeupAddMemberModal
        open={addMemberOpen}
        sessionId={selectedSession?.id ?? null}
        onClose={() => setAddMemberOpen(false)}
        onAdded={() => setRosterRefreshKey((key) => key + 1)}
      />
    </DraggableModal>
  );
}
